import { useCallback, useEffect, useState } from 'react';
import { api } from '../api/client';
import { KeycloakEventFeed } from '../components/KeycloakEventFeed';

const TYPE_GROUPS: Array<{ label: string; types: string[] }> = [
  {
    label: 'Sign-in',
    types: [
      'LOGIN',
      'LOGOUT',
      'CODE_TO_TOKEN',
      'REFRESH_TOKEN',
      'CLIENT_LOGIN',
      'IDENTITY_PROVIDER_LOGIN',
      'INTROSPECT_TOKEN',
    ],
  },
  {
    label: 'Account',
    types: [
      'REGISTER',
      'UPDATE_PROFILE',
      'UPDATE_EMAIL',
      'UPDATE_PASSWORD',
      'UPDATE_CREDENTIAL',
      'REMOVE_CREDENTIAL',
      'UPDATE_TOTP',
      'REMOVE_TOTP',
      'VERIFY_EMAIL',
      'SEND_VERIFY_EMAIL',
      'SEND_RESET_PASSWORD',
      'RESET_PASSWORD',
      'EXECUTE_ACTIONS',
      'DELETE_ACCOUNT',
    ],
  },
  {
    label: 'Failures',
    types: [
      'LOGIN_ERROR',
      'LOGOUT_ERROR',
      'CODE_TO_TOKEN_ERROR',
      'REFRESH_TOKEN_ERROR',
      'CLIENT_LOGIN_ERROR',
      'REGISTER_ERROR',
      'UPDATE_PASSWORD_ERROR',
      'RESET_PASSWORD_ERROR',
      'EXECUTE_ACTIONS_ERROR',
      'IDENTITY_PROVIDER_LOGIN_ERROR',
    ],
  },
];

const PAGE_SIZES = [25, 50, 100, 200];

export function EventsPage() {
  const [search, setSearch] = useState('');
  const [applied, setApplied] = useState('');
  const [type, setType] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [pageSize, setPageSize] = useState(50);

  useEffect(() => {
    const handle = window.setTimeout(() => setApplied(search.trim()), 350);
    return () => window.clearTimeout(handle);
  }, [search]);

  const load = useCallback(
    (first: number, max: number) =>
      api.keycloakEvents({
        first,
        max,
        type: type || undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        search: applied || undefined,
      }),
    [type, dateFrom, dateTo, applied],
  );

  const filterKey = `${type}|${dateFrom}|${dateTo}|${applied}|${pageSize}`;
  const filtered = Boolean(type || dateFrom || dateTo || applied);

  return (
    <div className="page page-full">
      <div className="page-header">
        <div>
          <h1>Keycloak events</h1>
          <div className="page-sub">
            Sign-ins, failures and account changes recorded by the Keycloak realm.
          </div>
        </div>
        <div className="page-header-actions">
          {filtered && (
            <button
              className="btn-secondary"
              onClick={() => {
                setSearch('');
                setApplied('');
                setType('');
                setDateFrom('');
                setDateTo('');
              }}
            >
              Clear filters
            </button>
          )}
        </div>
      </div>

      <div className="toolbar toolbar-wrap">
        <div className="search-wrap">
          <svg width="15" height="15" viewBox="0 0 16 16" fill="none" className="search-icon">
            <circle cx="7" cy="7" r="4.5" stroke="currentColor" strokeWidth="1.5" />
            <path d="M10.5 10.5L14 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
          <input
            className="search-input"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search user, client, IP, error or event detail…"
          />
        </div>
        <div className="filter-row">
          <label className="filter-field">
            <span className="filter-label">Event type</span>
            <select
              className="filter-input"
              value={type}
              onChange={(event) => setType(event.target.value)}
            >
              <option value="">All events</option>
              {TYPE_GROUPS.map((group) => (
                <optgroup key={group.label} label={group.label}>
                  {group.types.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </label>
          <label className="filter-field">
            <span className="filter-label">From</span>
            <input
              className="filter-input"
              type="date"
              value={dateFrom}
              onChange={(event) => setDateFrom(event.target.value)}
            />
          </label>
          <label className="filter-field">
            <span className="filter-label">To</span>
            <input
              className="filter-input"
              type="date"
              value={dateTo}
              onChange={(event) => setDateTo(event.target.value)}
            />
          </label>
          <label className="filter-field">
            <span className="filter-label">Per page</span>
            <select
              className="filter-input"
              value={pageSize}
              onChange={(event) => setPageSize(Number(event.target.value))}
            >
              {PAGE_SIZES.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      <div className="card table-card">
        <KeycloakEventFeed
          key={filterKey}
          load={load}
          pageSize={pageSize}
          showUser
          emptyLabel={
            filtered
              ? 'No Keycloak events match these filters.'
              : 'No Keycloak events — check that event logging is enabled in the realm.'
          }
        />
      </div>
    </div>
  );
}
